import { useState } from 'react';
import { Alert, api, ApiError, Badge, Button, Card, fmtDate, Input, ListPage, Select, Spinner, useAuth, useFetch, usePagedList } from '@greenrinse/ui';

interface ProviderStatus { provider: 'Xero' | 'QuickBooks'; configured: boolean; connected: boolean; status: string | null; orgName: string | null; connectedAtUtc: string | null; lastSyncAtUtc: string | null; lastError: string | null; synced: number; failed: number }
interface Status { providers: ProviderStatus[]; autoSync: boolean }
interface Link { id: string; provider: string; entityType: string; entityId: string; entityRef: string | null; externalId: string | null; externalNumber: string | null; status: 'Synced' | 'Failed'; attempts: number; syncedAtUtc: string | null; lastError: string | null }
interface SettingDto { key: string; value: string | null; isSet: boolean }

const META: Record<string, { icon: string; blurb: string; fields: { key: string; label: string; secret?: boolean; hint?: string }[] }> = {
  Xero: {
    icon: '🔵', blurb: 'Invoices are created as AUTHORISED sales invoices; credit notes as credit notes. Create an OAuth 2.0 app at developer.xero.com.',
    fields: [
      { key: 'Accounting.Xero.ClientId', label: 'Client ID' },
      { key: 'Accounting.Xero.ClientSecret', label: 'Client secret', secret: true },
      { key: 'Accounting.Xero.SalesAccountCode', label: 'Sales account code', hint: 'Chart-of-accounts code used on invoice lines (default 200).' },
    ],
  },
  QuickBooks: {
    icon: '🟢', blurb: 'Invoices and credit memos are created against a service Item. Create an app at developer.intuit.com.',
    fields: [
      { key: 'Accounting.Qbo.ClientId', label: 'Client ID' },
      { key: 'Accounting.Qbo.ClientSecret', label: 'Client secret', secret: true },
      { key: 'Accounting.Qbo.ItemId', label: 'Service Item id', hint: 'The QuickBooks Item used on lines (e.g. "1" or your "Services" item).' },
      { key: 'Accounting.Qbo.Environment', label: 'Environment', hint: 'production or sandbox' },
    ],
  },
};

function ProviderCard({ p, isSuperAdmin, onChanged }: { p: ProviderStatus; isSuperAdmin: boolean; onChanged: () => void }) {
  const meta = META[p.provider];
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const settings = useFetch<SettingDto[]>(editing ? '/api/settings' : null);

  const connect = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api<{ url: string }>(`/api/accounting/connect/${p.provider}`, { method: 'POST' });
      window.open(r.url, '_blank', 'width=640,height=760');
      setTimeout(onChanged, 4000); // refresh status after the user returns; they can also reload manually
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${p.provider}? Synced links are kept; new documents will no longer be pushed.`)) return;
    await api(`/api/accounting/disconnect/${p.provider}`, { method: 'POST' }); onChanged();
  };
  const saveConfig = async () => {
    setBusy(true); setError(null);
    try { await api('/api/settings', { method: 'PUT', body: { values: form } }); setEditing(false); setForm({}); onChanged(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Failed'); } finally { setBusy(false); }
  };

  return (
    <Card small>
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <div><strong style={{ fontSize: 16 }}>{meta.icon} {p.provider}</strong>
          <div className="gr-muted" style={{ fontSize: 13 }}>{p.connected ? <>Connected to <strong>{p.orgName}</strong> · since {fmtDate(p.connectedAtUtc!, { dateStyle: 'medium' })}</> : meta.blurb}</div>
        </div>
        <div>
          {p.connected && p.status === 'Connected' && <Badge tone="success">Connected</Badge>}
          {p.status === 'Error' && <Badge tone="error">Needs re-connect</Badge>}
          {!p.connected && !p.configured && <Badge>Not configured</Badge>}
        </div>
      </div>
      {p.lastError && <div style={{ marginTop: 8 }}><Alert tone="error">{p.lastError}</Alert></div>}
      {error && <div style={{ marginTop: 8 }}><Alert tone="error">{error}</Alert></div>}
      <div className="gr-row" style={{ marginTop: 12 }}>
        {isSuperAdmin && <Button variant="outline" style={{ minHeight: 34 }} onClick={() => setEditing(!editing)}>{editing ? 'Close' : 'Configure'}</Button>}
        {isSuperAdmin && p.configured && <Button loading={busy} style={{ minHeight: 34 }} onClick={() => void connect()}>{p.connected ? 'Re-connect' : `Connect ${p.provider}`}</Button>}
        {isSuperAdmin && p.connected && <Button variant="ghost" style={{ minHeight: 34 }} onClick={() => void disconnect()}>Disconnect</Button>}
        <span className="gr-topbar__spacer" />
        {(p.synced > 0 || p.failed > 0) && <span className="gr-muted" style={{ fontSize: 13 }}>{p.synced} synced{p.failed > 0 ? ` · ${p.failed} failed` : ''}{p.lastSyncAtUtc ? ` · last ${fmtDate(p.lastSyncAtUtc)}` : ''}</span>}
      </div>
      {editing && (settings.loading ? <Spinner /> : (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--gr-border)', paddingTop: 12 }}>
          <p className="gr-muted" style={{ marginTop: 0, fontSize: 13 }}>Redirect URI for the {p.provider} app: <code>{window.location.origin.replace(/:\d+$/, '')}…/api/accounting/callback/{p.provider.toLowerCase()}</code> (your API origin).</p>
          {meta.fields.map((f) => {
            const current = settings.data?.find((s) => s.key === f.key);
            return <Input key={f.key} label={f.label} hint={f.hint} type={f.secret ? 'password' : 'text'} autoComplete="off"
              placeholder={f.secret && current?.isSet ? '••••••••' : ''} value={form[f.key] ?? (f.secret ? '' : current?.value ?? '')}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />;
          })}
          <Button loading={busy} onClick={() => void saveConfig()}>Save configuration</Button>
        </div>
      ))}
    </Card>
  );
}

/** Admin → Accounting: connect Xero / QuickBooks, auto-sync toggle, and the push log. */
export default function AccountingPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin';
  const status = useFetch<Status>('/api/accounting/status');
  const [filter, setFilter] = useState('');
  const links = usePagedList<Link>('/api/accounting/links', { status: filter || undefined }, 25);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const toggleAutoSync = async (on: boolean) => {
    try { await api('/api/settings', { method: 'PUT', body: { values: { 'Accounting.AutoSyncInvoices': String(on) } } }); status.reload(); }
    catch (e) { setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : 'Only a Super Admin can change this.' }); }
  };
  const retry = async (l: Link) => {
    setRetrying(l.id); setMsg(null);
    try {
      const path = l.entityType === 'CreditNote' ? `/api/accounting/sync/credit-note/${l.entityId}` : `/api/accounting/sync/invoice/${l.entityId}`;
      const r = await api<Link>(path, { method: 'POST' });
      setMsg(r.status === 'Synced' ? { tone: 'success', text: `${l.entityRef} pushed.` } : { tone: 'error', text: r.lastError ?? 'Failed again.' });
      links.reload(); status.reload();
    } catch (e) { setMsg({ tone: 'error', text: e instanceof ApiError ? e.message : 'Failed' }); } finally { setRetrying(null); }
  };

  if (status.loading && !status.data) return <Spinner />;
  return (
    <div className="gr-stack">
      <div className="gr-row" style={{ justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0 }}>🧾 Accounting</h1>
        <label className="gr-row" style={{ gap: 8 }}>
          <input type="checkbox" disabled={!isSuperAdmin} checked={status.data?.autoSync ?? false} onChange={(e) => void toggleAutoSync(e.target.checked)} />
          Auto-push issued invoices & credit notes (hourly)
        </label>
      </div>
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <div className="gr-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))' }}>
        {(status.data?.providers ?? []).map((p) => <ProviderCard key={p.provider} p={p} isSuperAdmin={isSuperAdmin} onChanged={() => status.reload()} />)}
      </div>
      <p className="gr-muted" style={{ fontSize: 13, margin: 0 }}>One system can be connected at a time. Documents are pushed with their GreenRinse numbers; the mapping (including each partner's customer record) is kept below. Setup guide: <code>docs/accounting-integration.md</code>.</p>

      <ListPage<Link>
        title="Sync log" state={links} rowKey={(l) => l.id}
        filters={<Select value={filter} onChange={(e) => setFilter(e.target.value)} options={[{ value: '', label: 'All' }, { value: 'Synced', label: 'Synced' }, { value: 'Failed', label: 'Failed' }]} style={{ width: 'auto', minHeight: 40 }} />}
        columns={[
          { key: 'doc', header: 'Document', render: (l) => <><strong>{l.entityRef ?? l.entityId.slice(0, 8)}</strong><div className="gr-muted" style={{ fontSize: 12 }}>{l.entityType}</div></> },
          { key: 'provider', header: 'System', render: (l) => l.provider },
          { key: 'external', header: 'External id', render: (l) => l.externalId ? <code style={{ fontSize: 12 }}>{l.externalId}</code> : '—' },
          { key: 'status', header: 'Status', render: (l) => <><Badge tone={l.status === 'Synced' ? 'success' : 'error'}>{l.status}</Badge>{l.lastError && <div style={{ fontSize: 12, color: 'var(--gr-error)' }}>{l.lastError.slice(0, 120)}</div>}</> },
          { key: 'when', header: 'Synced', render: (l) => l.syncedAtUtc ? fmtDate(l.syncedAtUtc) : `${l.attempts} attempt(s)` },
          { key: 'actions', header: '', render: (l) => l.status === 'Failed' ? <Button variant="ghost" loading={retrying === l.id} style={{ minHeight: 30, padding: '0 8px' }} onClick={() => void retry(l)}>Retry</Button> : null, align: 'end' },
        ]}
      />
    </div>
  );
}
